import { getAccessToken } from './supabase';
import type {
  CleanPlate,
  CreateCleanPlateRequest,
  CreateExportRequest,
  CreateExportResponse,
  CreateGarageCarRequest,
  CreateProductionRequest,
  CreateSourceClipRequest,
  DraftCleanPlatePromptResponse,
  DraftSourceClipPromptRequest,
  GarageAngle,
  GarageCarWithSignedUrls,
  GarageUploadSignResponse,
  ListExportsResponse,
  LocationImage,
  LocationImageWithUsage,
  LocationViewMode,
  Production,
  ProductionStatus,
  RegenerateRequest,
  RegenerateSourceClipRequest,
  SceneForgeJob,
  SceneForgeTeam,
  SceneForgeUserMe,
  SourceClip,
  SourceClipEvent,
  TeamMember,
  UpdateGarageCarRequest,
  UpdateProductionRequest,
  UploadsSignResponse,
  UsageSummary,
} from './types';

// SceneForge API wrapper.
//
// Defense-in-depth: every request is required to start with `/v1/sceneforge/`.
// The Worker handler at `worker/autoai-proxy/src/routes/sceneforge.ts` will
// 404 anything else anyway, but the runtime assert here catches programming
// errors (typos, accidental copy-paste from a consumer-app PR) before they
// reach the wire and helps reviewers see violations in red during PR diffs.

const PROXY_URL = import.meta.env.VITE_PROXY_URL;

if (!PROXY_URL) {
  throw new Error(
    '[SCENEFORGE] Missing VITE_PROXY_URL. Expected something like ' +
      '`https://api.autoai.studio` or `https://staging-api.autoai.studio`.',
  );
}

const SCENEFORGE_PATH_PREFIX = '/v1/sceneforge/';

// ---------------------------------------------------------------------------
// V5 — active team header
//
// `teamContext.ts` (or any caller switching teams) writes the active
// team_id here via `setActiveTeamId`. Every request below auto-attaches
// `X-Sceneforge-Team: <team_id>` when a team is set — the Worker uses
// the header (with body/query falling back) to drive `verifyTeamAccess`.
//
// Kept as a module-level variable instead of going through localStorage
// on every request to avoid the synchronous read on the hot path; the
// teamContext provider mirrors localStorage <-> this variable.
// ---------------------------------------------------------------------------

let ACTIVE_TEAM_ID: string | null = null;

export function setActiveTeamId(teamId: string | null): void {
  ACTIVE_TEAM_ID = teamId && teamId.length > 0 ? teamId : null;
}

export function getActiveTeamId(): string | null {
  return ACTIVE_TEAM_ID;
}

export class SceneForgeApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'SceneForgeApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Translate a worker-side error from the source-clip dispatch path into
 * a friendly partner-facing message. Returns `null` when the error is
 * not one of the known dispatch codes so the caller can fall back to
 * its generic copy.
 *
 * Today this covers the 429 ``too_many_concurrent_clips`` (team is at
 * the Modal concurrency cap) but the helper is intentionally a
 * single switch so future dispatch-time codes (e.g. ``max_attempts_exceeded``)
 * land in one place instead of being re-translated in every page.
 * Added 2026-05-27 (new batch) alongside the worker rate-limit bump.
 */
export function friendlyClipDispatchError(err: unknown): string | null {
  if (!(err instanceof SceneForgeApiError)) return null;
  const body = err.body;
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const code = String((body as { error: unknown }).error);
  const meta = body as { meta?: { max?: number; current?: number } };
  if (code === 'too_many_concurrent_clips') {
    const max = typeof meta.meta?.max === 'number' ? meta.meta.max : 5;
    const cur = typeof meta.meta?.current === 'number' ? meta.meta.current : max;
    return `Your team is at the ${max}-clip concurrent generation cap (${cur}/${max}). Wait for one of the in-flight clips to finish, then try again — the queued clip will dispatch automatically.`;
  }
  if (code === 'max_attempts_exceeded') {
    return 'This clip has hit its 5-attempt retry cap. Open a fresh source clip from the wizard.';
  }
  return null;
}

/**
 * Translates worker errors from the Delete-Location-Image endpoint
 * into partner-facing strings. Mirrors `friendlyClipDispatchError`
 * so error mapping for every "destructive action" lives in one
 * file. Returns ``null`` for codes we don't have a friendly string
 * for so callers can fall back to a generic message.
 */
export function friendlyLocationImageDeleteError(err: unknown): string | null {
  if (!(err instanceof SceneForgeApiError)) return null;
  const body = err.body;
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const code = String((body as { error: unknown }).error);
  if (code === 'location_image_in_use') {
    const count = (body as { ready_clip_count?: number }).ready_clip_count;
    const plural = count === 1 ? 'clip' : 'clips';
    return count
      ? `Can't delete — this image is still used by ${count} finished ${plural} in your jobs. Delete the clips first if you really want to free this image.`
      : "Can't delete — this image is still used by a finished clip in one of your jobs.";
  }
  if (code === 'forbidden') {
    return "You don't have permission to delete this image.";
  }
  if (code === 'not_found') {
    return 'This image has already been deleted. Refreshing the page…';
  }
  return null;
}

/**
 * Translates worker errors from the standalone Clean Plate flow into
 * partner-facing strings. Mirrors `friendlyClipDispatchError` — same
 * concurrency cap pattern, different bucket.
 */
export function friendlyCleanPlateDispatchError(err: unknown): string | null {
  if (!(err instanceof SceneForgeApiError)) return null;
  const body = err.body;
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const code = String((body as { error: unknown }).error);
  const meta = body as { meta?: { max?: number; current?: number } };
  if (code === 'too_many_concurrent_clean_plates') {
    const max = typeof meta.meta?.max === 'number' ? meta.meta.max : 5;
    const cur = typeof meta.meta?.current === 'number' ? meta.meta.current : max;
    return `Your team is at the ${max}-clean-plate concurrent generation cap (${cur}/${max}). Wait for one of the in-flight plates to finish.`;
  }
  if (code === 'max_attempts_exceeded') {
    return 'This clean plate has hit its 5-attempt retry cap. Delete it and create a fresh one from the source clip.';
  }
  if (code === 'source_clip_not_ready') {
    return 'The source clip is still generating or failed. Pick a finished clip.';
  }
  if (code === 'source_clip_not_found') {
    return "Couldn't find the source clip you picked. Refresh the job page and try again.";
  }
  if (code === 'source_clip_missing_r2_key') {
    return "The source clip's video file is missing. Try a different clip.";
  }
  return null;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** When true, do not throw on non-2xx — return the parsed body so the caller
   *  can branch on status (e.g. the 409 in-flight gate). */
  allowNon2xx?: boolean;
  signal?: AbortSignal;
}

interface RequestResult<T> {
  ok: boolean;
  status: number;
  data: T;
}

async function request<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<RequestResult<T>> {
  if (!path.startsWith(SCENEFORGE_PATH_PREFIX)) {
    // Hard runtime assert — see file header.
    throw new Error(
      `[SCENEFORGE] api() refused non-sceneforge path "${path}". ` +
        `Every SceneForge request must start with "${SCENEFORGE_PATH_PREFIX}".`,
    );
  }

  const token = await getAccessToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  // V5: auto-attach the active team id when set. Worker reads this header
  // and uses it as the primary source of truth for `verifyTeamAccess`.
  if (ACTIVE_TEAM_ID) {
    headers['X-Sceneforge-Team'] = ACTIVE_TEAM_ID;
  }

  const fetchOpts: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'omit',
  };
  if (opts.body !== undefined) {
    fetchOpts.body = JSON.stringify(opts.body);
  }
  if (opts.signal) {
    fetchOpts.signal = opts.signal;
  }

  const res = await fetch(`${PROXY_URL}${path}`, fetchOpts);
  const contentType = res.headers.get('content-type') ?? '';

  let parsed: unknown;
  if (contentType.includes('application/json')) {
    parsed = await res.json().catch(() => null);
  } else {
    parsed = await res.text().catch(() => '');
  }

  if (!res.ok && !opts.allowNon2xx) {
    const message =
      typeof parsed === 'object' && parsed !== null && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : res.statusText || `HTTP ${res.status}`;
    throw new SceneForgeApiError(res.status, message, parsed);
  }

  return { ok: res.ok, status: res.status, data: parsed as T };
}

// ---------------------------------------------------------------------------
// Typed convenience wrappers — every caller in the app should use these
// rather than reaching for `request()` directly.
// ---------------------------------------------------------------------------

export const api = {
  async me(signal?: AbortSignal): Promise<SceneForgeUserMe> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<SceneForgeUserMe>('/v1/sceneforge/me', opts);
    return data;
  },

  async listExports(signal?: AbortSignal): Promise<SceneForgeJob[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<ListExportsResponse | SceneForgeJob[]>(
      '/v1/sceneforge/exports',
      opts,
    );
    // Worker returns either `{ exports: [...] }` or a bare array depending on
    // pagination wiring — accept both.
    if (Array.isArray(data)) return data;
    return data.exports ?? [];
  },

  /** Paginated listing for the Dashboard card grid. Returns `{ exports,
   *  has_more, total }` so the infinite-scroll loader knows when to stop.
   *  Worker presigns each row's `source_r2_key` into `source_signed_url`
   *  for the card video preview. */
  async listExportsPaged(
    args: { limit: number; offset: number; productionId?: string },
    signal?: AbortSignal,
  ): Promise<{
    exports: SceneForgeJob[];
    has_more: boolean;
    total: number;
  }> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const qs = new URLSearchParams({
      limit: String(args.limit),
      offset: String(args.offset),
    });
    if (args.productionId) {
      qs.set('productionId', args.productionId);
    }
    const { data } = await request<ListExportsResponse | SceneForgeJob[]>(
      `/v1/sceneforge/exports?${qs.toString()}`,
      opts,
    );
    if (Array.isArray(data)) {
      return { exports: data, has_more: false, total: data.length };
    }
    return {
      exports: data.exports ?? [],
      has_more: Boolean(data.has_more),
      total: typeof data.total === 'number'
        ? data.total
        : (data.exports?.length ?? 0),
    };
  },

  async getExport(id: string, signal?: AbortSignal): Promise<SceneForgeJob> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<SceneForgeJob>(
      `/v1/sceneforge/exports/${encodeURIComponent(id)}`,
      opts,
    );
    return data;
  },

  async signUpload(input: {
    filename: string;
    size_bytes: number;
    content_type: string;
  }): Promise<UploadsSignResponse> {
    const { data } = await request<UploadsSignResponse>(
      '/v1/sceneforge/uploads/sign',
      { method: 'POST', body: input },
    );
    return data;
  },

  /** Create a new export. Resolves to `{ id }` on success.
   *  When the user already has an in-flight export, throws a
   *  `SceneForgeApiError` with `status === 409` whose `body` carries the
   *  existing job id — callers should detect this and route the partner to
   *  the existing export instead of erroring noisily. */
  async createExport(input: CreateExportRequest): Promise<CreateExportResponse> {
    const { data } = await request<CreateExportResponse>(
      '/v1/sceneforge/exports',
      { method: 'POST', body: input },
    );
    return data;
  },

  async regenerate(id: string, body: RegenerateRequest): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/exports/${encodeURIComponent(id)}/regenerate`,
      { method: 'POST', body },
    );
  },

  async cancelExport(id: string): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/exports/${encodeURIComponent(id)}/cancel`,
      { method: 'POST' },
    );
  },

  /** V3.1 clean-plate approval gate. Hits
   *  `POST /v1/sceneforge/exports/:id/clean-plate/approve|reject`
   *  to release the orchestrator's wait loop. Idempotent on the
   *  worker side: pressing approve twice from a stale tab returns
   *  `{ok: true, idempotent: true}` instead of failing. */
  async cleanPlateApprove(id: string): Promise<{ ok: true; state: string }> {
    const { data } = await request<{ ok: true; state: string }>(
      `/v1/sceneforge/exports/${encodeURIComponent(id)}/clean-plate/approve`,
      { method: 'POST' },
    );
    return data;
  },
  async cleanPlateReject(id: string): Promise<{ ok: true; state: string }> {
    const { data } = await request<{ ok: true; state: string }>(
      `/v1/sceneforge/exports/${encodeURIComponent(id)}/clean-plate/reject`,
      { method: 'POST' },
    );
    return data;
  },

  /** Fetches a fresh one-time signed URL for a deliverable. The Worker mints
   *  it server-side; the client just follows the redirect (or reads the URL
   *  from the JSON body, depending on Worker convention). */
  async getDownloadUrl(id: string, deliverable: string): Promise<string> {
    const { data } = await request<{ url: string } | string>(
      `/v1/sceneforge/exports/${encodeURIComponent(id)}/download/${encodeURIComponent(deliverable)}`,
    );
    if (typeof data === 'string') return data;
    return data.url;
  },

  async acceptInvite(input: {
    token: string;
    email: string;
  }): Promise<{ ok: true }> {
    const { data } = await request<{ ok: true }>(
      '/v1/sceneforge/accept-invite',
      { method: 'POST', body: input },
    );
    return data;
  },

  /** Forwards a failure report to the Worker, which formats it and ships it
   *  to ``support@autoai.studio`` via Resend. The payload is small and
   *  vendor-agnostic — error code + stage + message + project metadata.
   *  No source media, no signed URLs. */
  async sendSupportReport(input: {
    job_id: string;
    project_name: string;
    error_code: string | null;
    error_stage: string | null;
    error_message: string | null;
  }): Promise<{ ok: true }> {
    const { data } = await request<{ ok: true }>(
      '/v1/sceneforge/support/report',
      { method: 'POST', body: input },
    );
    return data;
  },

  // -------------------------------------------------------------------------
  // V5 — Teams
  // -------------------------------------------------------------------------

  /** List every team the calling user belongs to (admins see all teams). */
  async listTeams(signal?: AbortSignal): Promise<SceneForgeTeam[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<{ teams: SceneForgeTeam[] }>(
      '/v1/sceneforge/teams',
      opts,
    );
    return data.teams ?? [];
  },

  /** Admin-only: create a brand-new team. */
  async createTeam(body: { name: string; slug?: string }): Promise<SceneForgeTeam> {
    const { data } = await request<SceneForgeTeam>(
      '/v1/sceneforge/teams',
      { method: 'POST', body },
    );
    return data;
  },

  async listTeamMembers(teamId: string, signal?: AbortSignal): Promise<TeamMember[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<{ members: TeamMember[] }>(
      `/v1/sceneforge/teams/${encodeURIComponent(teamId)}/members`,
      opts,
    );
    return data.members ?? [];
  },

  /** Admin-only: add a user to a team. If the email already maps to a
   *  `sceneforge_users` row (or any `auth.users` row), the user is added
   *  immediately (`{ added: true, mode: 'direct' }`); otherwise an invite
   *  is emailed via Resend (`{ invited: true, mode: 'invited',
   *  email_sent: boolean }`). */
  async addTeamMember(
    teamId: string,
    body: { email: string; role?: 'admin' | 'member' },
  ): Promise<{
    added?: boolean;
    invited?: boolean;
    mode?: 'direct' | 'invited';
    email_sent?: boolean;
    member?: TeamMember;
  }> {
    const { data } = await request<{
      added?: boolean;
      invited?: boolean;
      mode?: 'direct' | 'invited';
      email_sent?: boolean;
      member?: TeamMember;
    }>(
      `/v1/sceneforge/teams/${encodeURIComponent(teamId)}/members`,
      { method: 'POST', body },
    );
    return data;
  },

  async removeTeamMember(teamId: string, userId: string): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    );
  },

  // -------------------------------------------------------------------------
  // V5 — Productions (Jobs)
  // -------------------------------------------------------------------------

  async listProductions(
    opts?: { status?: ProductionStatus | 'all' },
    signal?: AbortSignal,
  ): Promise<Production[]> {
    const reqOpts: RequestOptions = {};
    if (signal) reqOpts.signal = signal;
    const qs = new URLSearchParams();
    if (opts?.status && opts.status !== 'all') {
      qs.set('status', opts.status);
    }
    const path = qs.toString()
      ? `/v1/sceneforge/productions?${qs.toString()}`
      : '/v1/sceneforge/productions';
    const { data } = await request<{ productions: Production[] }>(path, reqOpts);
    return data.productions ?? [];
  },

  async createProduction(body: CreateProductionRequest): Promise<{ id: string }> {
    const { data } = await request<{ id: string }>(
      '/v1/sceneforge/productions',
      { method: 'POST', body },
    );
    return data;
  },

  async getProduction(id: string, signal?: AbortSignal): Promise<Production> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<Production>(
      `/v1/sceneforge/productions/${encodeURIComponent(id)}`,
      opts,
    );
    return data;
  },

  async updateProduction(id: string, body: UpdateProductionRequest): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/productions/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    );
  },

  async deleteProduction(id: string): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/productions/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },

  // -------------------------------------------------------------------------
  // V5 — Garage
  // -------------------------------------------------------------------------

  async listGarageCars(signal?: AbortSignal): Promise<GarageCarWithSignedUrls[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<{ cars: GarageCarWithSignedUrls[] }>(
      '/v1/sceneforge/garage',
      opts,
    );
    return data.cars ?? [];
  },

  async getGarageCar(id: string, signal?: AbortSignal): Promise<GarageCarWithSignedUrls> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<GarageCarWithSignedUrls>(
      `/v1/sceneforge/garage/${encodeURIComponent(id)}`,
      opts,
    );
    return data;
  },

  async signGarageUpload(body: {
    car_id?: string;
    angle: GarageAngle;
    filename: string;
    content_type: string;
    size_bytes: number;
  }): Promise<GarageUploadSignResponse> {
    const { data } = await request<GarageUploadSignResponse>(
      '/v1/sceneforge/garage/uploads/sign',
      { method: 'POST', body },
    );
    return data;
  },

  async createGarageCar(body: CreateGarageCarRequest): Promise<{ id: string }> {
    const { data } = await request<{ id: string }>(
      '/v1/sceneforge/garage',
      { method: 'POST', body },
    );
    return data;
  },

  async updateGarageCar(id: string, body: UpdateGarageCarRequest): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/garage/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    );
  },

  async deleteGarageCar(id: string): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/garage/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },

  // -------------------------------------------------------------------------
  // V5 — Location Images (Gemini)
  // -------------------------------------------------------------------------

  async listLocationImages(productionId: string, signal?: AbortSignal): Promise<LocationImage[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    // Worker historically returned `{ location_images: [...] }` while the
    // contract called for `{ images: [...] }`. Accept both so older worker
    // builds keep working during a staggered deploy.
    const { data } = await request<{ images?: LocationImage[]; location_images?: LocationImage[] }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/location-images`,
      opts,
    );
    return data.images ?? data.location_images ?? [];
  },

  /** Kicks off Gemini-driven location image generation under a specific
   *  production. Returns the fully-resolved row (`status='ready'` + signed
   *  url) on success — the worker waits synchronously for Gemini.
   *
   *  When `referenceR2Key` is supplied, the worker pre-fetches the
   *  uploaded reference photo from R2 and passes it to Nano Banana
   *  as an additional inlineData part — used as a ROUGH visual
   *  anchor (composition / mood / lighting) only.
   *
   *  When `sourceLocationImageId` is supplied (the "Modify location"
   *  flow on JobDetail — added 2026-05-27, new batch), the worker
   *  resolves the existing location-image row, verifies team
   *  ownership, fetches its bytes from R2, and uses THAT as the
   *  Nano Banana reference instead of a separately-uploaded photo.
   *  The two are mutually exclusive; source_location_image_id wins.
   *
   *  When `viewMode === 'aerial'` (migration 189) the worker uses the
   *  drone-altitude guidance instead of the ground-level default —
   *  typically combined with `sourceLocationImageId` so the aerial
   *  plate reads as the same location as an existing ground plate. */
  async generateLocationImage(
    productionId: string,
    description: string,
    referenceR2Key?: string | null,
    sourceLocationImageId?: string | null,
    viewMode?: LocationViewMode,
  ): Promise<LocationImage> {
    const body: {
      description: string;
      reference_r2_key?: string;
      source_location_image_id?: string;
      view_mode?: LocationViewMode;
    } = { description };
    if (referenceR2Key) body.reference_r2_key = referenceR2Key;
    if (sourceLocationImageId) body.source_location_image_id = sourceLocationImageId;
    if (viewMode === 'aerial') body.view_mode = viewMode;
    const { data } = await request<LocationImage>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/location-images`,
      { method: 'POST', body },
    );
    return data;
  },

  /** Team-wide list of every location image (production-scoped + standalone),
   *  enriched with `used_by` so the Locations page can flag rows that are
   *  bound to a successful source clip. */
  async listLocations(signal?: AbortSignal): Promise<LocationImageWithUsage[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<{ images: LocationImageWithUsage[] }>(
      '/v1/sceneforge/locations',
      opts,
    );
    return data.images ?? [];
  },

  /** "Add from library" — re-parents an existing team location image
   *  into the supplied production so it appears in that job's
   *  "Generated Locations" gallery. Server-side re-parenting (no row
   *  copy) so usage events are NOT double-billed. The image disappears
   *  from its previous production's gallery — partner is aware of and
   *  accepts this trade-off (vs. a more invasive many-to-many join). */
  async attachLocationImageToProduction(
    productionId: string,
    locationImageId: string,
  ): Promise<LocationImage> {
    const { data } = await request<LocationImage>(
      `/v1/sceneforge/productions/${productionId}/location-images/attach`,
      { method: 'POST', body: { location_image_id: locationImageId } },
    );
    return data;
  },

  /** Creates a standalone team-wide location image (production_id=NULL).
   *  Same Gemini path as the per-production endpoint; resolves with the
   *  ready row including `used_by: []`. Optional `referenceR2Key`
   *  points at a previously-uploaded reference photo (see
   *  `signLocationReferenceUpload`) — Nano Banana will use it as a
   *  rough visual anchor only. */
  async generateTeamLocationImage(
    description: string,
    referenceR2Key?: string | null,
  ): Promise<LocationImageWithUsage> {
    const body: { description: string; reference_r2_key?: string } = { description };
    if (referenceR2Key) body.reference_r2_key = referenceR2Key;
    const { data } = await request<LocationImageWithUsage>(
      '/v1/sceneforge/locations',
      { method: 'POST', body },
    );
    return data;
  },

  /** Hard-delete an UNUSED team-wide location image — DB row + R2
   *  object. The worker refuses (409 ``location_image_in_use``) if
   *  any source clip in status 'ready' still references the image,
   *  so the caller should only enable the Delete affordance for rows
   *  with ``used_by.length === 0`` (matches the Locations page's
   *  Unused filter). Throws ``SceneForgeApiError`` on any non-2xx;
   *  use ``friendlyLocationImageDeleteError`` to translate. */
  async deleteLocationImage(locationImageId: string): Promise<void> {
    await request<{ ok: true; r2_deleted: boolean }>(
      `/v1/sceneforge/locations/${locationImageId}`,
      { method: 'DELETE' },
    );
  },

  /** Mint a one-shot R2 PUT URL for a reference photo the user wants
   *  Nano Banana to consult on the next location-image generation. The
   *  returned `r2_key` is what you pass to `generateLocationImage` /
   *  `generateTeamLocationImage`. */
  async signLocationReferenceUpload(body: {
    filename: string;
    content_type: string;
    size_bytes: number;
  }): Promise<{ url: string; r2_key: string; expires_at: string }> {
    const { data } = await request<{ url: string; r2_key: string; expires_at: string }>(
      '/v1/sceneforge/locations/uploads/sign',
      { method: 'POST', body },
    );
    return data;
  },

  // -------------------------------------------------------------------------
  // V5 — Source Clips (Seedance)
  // -------------------------------------------------------------------------

  async listSourceClips(productionId: string, signal?: AbortSignal): Promise<SourceClip[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    // Worker emits { source_clips: [...] }. An earlier draft of this
    // method read `data.clips` which silently produced an empty list —
    // the JobDetail page showed "No source clips" even when usage
    // reported clips in flight. Accept both keys defensively so a
    // contract drift in either direction can't break the gallery.
    const { data } = await request<{
      source_clips?: SourceClip[];
      clips?: SourceClip[];
    }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips`,
      opts,
    );
    return data.source_clips ?? data.clips ?? [];
  },

  async getSourceClip(
    productionId: string,
    clipId: string,
    signal?: AbortSignal,
  ): Promise<SourceClip> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<SourceClip>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/${encodeURIComponent(clipId)}`,
      opts,
    );
    return data;
  },

  /** Step 2 of the wizard: ask Gemini 2.5-flash to draft the Seedance
   *  prompt from the picked car + location + action. Returns the prompt
   *  text only — no DB rows are written. */
  async draftSourceClipPrompt(
    productionId: string,
    body: DraftSourceClipPromptRequest,
  ): Promise<{ prompt: string }> {
    const { data } = await request<{ prompt: string }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/draft-prompt`,
      { method: 'POST', body },
    );
    return data;
  },

  /** Step 3 of the wizard: persist the clip row (status='queued')
   *  with both `generated_prompt` and `final_prompt`. Does NOT dispatch
   *  to Modal — call `generateSourceClip` next. */
  async createSourceClip(
    productionId: string,
    body: CreateSourceClipRequest,
  ): Promise<{ id: string }> {
    const { data } = await request<{ id: string }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips`,
      { method: 'POST', body },
    );
    return data;
  },

  /** Dispatches a queued clip to Modal. Idempotent on the worker side
   *  (a second call while the row is already `generating` returns
   *  `{ ok: true, already_in_flight: true }`). */
  async generateSourceClip(
    productionId: string,
    clipId: string,
  ): Promise<{ ok: boolean; already_in_flight?: boolean }> {
    const { data } = await request<{ ok: boolean; already_in_flight?: boolean }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/${encodeURIComponent(clipId)}/generate`,
      { method: 'POST' },
    );
    return data;
  },

  /** Re-dispatch a failed/ready clip. Optionally overrides `final_prompt`
   *  before the new attempt. Worker enforces the 5-attempt cap. */
  async regenerateSourceClip(
    productionId: string,
    clipId: string,
    body?: RegenerateSourceClipRequest,
  ): Promise<{ ok: boolean }> {
    const opts: RequestOptions = { method: 'POST' };
    if (body) opts.body = body;
    const { data } = await request<{ ok: boolean }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/${encodeURIComponent(clipId)}/regenerate`,
      opts,
    );
    return data;
  },

  async listSourceClipEvents(
    productionId: string,
    clipId: string,
    signal?: AbortSignal,
  ): Promise<SourceClipEvent[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<{ events: SourceClipEvent[] }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/${encodeURIComponent(clipId)}/events`,
      opts,
    );
    return data.events ?? [];
  },

  async deleteSourceClip(productionId: string, clipId: string): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/${encodeURIComponent(clipId)}`,
      { method: 'DELETE' },
    );
  },

  /** Email a "bad generation" report to support@autoai.studio with the
   *  final prompt + a 7-day signed clip URL. Best-effort — the worker
   *  rate-limits to 5 reports / hour per (team, clip). */
  async reportBadSourceClip(
    productionId: string,
    clipId: string,
    body?: { note?: string },
  ): Promise<{ ok: boolean; sent?: boolean }> {
    const opts: RequestOptions = { method: 'POST' };
    if (body) opts.body = body;
    const { data } = await request<{ ok: boolean; sent?: boolean }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/${encodeURIComponent(clipId)}/report`,
      opts,
    );
    return data;
  },

  /** Dispatch a car-swap pipeline run that produces a NEW source clip
   *  with the target garage car composited into the original clip's
   *  motion + scene. The resulting clip is tagged `is_car_swap=true`
   *  for the UI badge and back-links to the source via
   *  `swapped_from_clip_id`. */
  async carSwapSourceClip(
    productionId: string,
    clipId: string,
    body: { garage_car_id: string },
  ): Promise<{ ok: boolean; clip_id: string }> {
    const { data } = await request<{ ok: boolean; clip_id: string }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/${encodeURIComponent(clipId)}/car-swap`,
      { method: 'POST', body },
    );
    return data;
  },

  // -------------------------------------------------------------------------
  // V5 — Upload your own clip (migration 169 + worker handlers
  // handleSourceClipUploadSign / handleSourceClipUploadCommit). Two-
  // step flow: SIGN returns a presigned PUT URL the browser uses to
  // stream the MP4 straight to R2; COMMIT inserts the row with
  // status='ready' + is_uploaded=true so it lands in the gallery
  // alongside Seedance-generated clips.
  // -------------------------------------------------------------------------

  async signSourceClipUpload(
    productionId: string,
    body: { filename: string; content_type: string; size_bytes: number },
  ): Promise<{ url: string; r2_key: string; expires_at: string }> {
    const { data } = await request<{ url: string; r2_key: string; expires_at: string }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/upload/sign`,
      { method: 'POST', body },
    );
    return data;
  },

  async commitSourceClipUpload(
    productionId: string,
    body: { r2_key: string; duration_seconds: 5 | 10; original_filename?: string },
  ): Promise<{ source_clip: { id: string } }> {
    const { data } = await request<{ source_clip: { id: string } }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/source-clips/upload/commit`,
      { method: 'POST', body },
    );
    return data;
  },

  // -------------------------------------------------------------------------
  // V5 — Clean Plates (standalone vehicle-removal, migration 170)
  // -------------------------------------------------------------------------

  async listCleanPlates(productionId: string, signal?: AbortSignal): Promise<CleanPlate[]> {
    const opts: RequestOptions = {};
    if (signal) opts.signal = signal;
    const { data } = await request<{ clean_plates?: CleanPlate[] }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/clean-plates`,
      opts,
    );
    return data.clean_plates ?? [];
  },

  /** Ask Gemini 2.5 Flash to merge the partner's special request into
   *  the standard vehicle-removal prompt. Only call this when the
   *  partner has typed a non-empty special request — for a standard
   *  plate the wizard can skip straight to `createCleanPlate`. */
  async draftCleanPlatePrompt(
    productionId: string,
    body: { special_request: string },
  ): Promise<DraftCleanPlatePromptResponse> {
    const { data } = await request<DraftCleanPlatePromptResponse>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/clean-plates/draft-prompt`,
      { method: 'POST', body },
    );
    return data;
  },

  /** Create + dispatch a clean plate in one call. The worker:
   *    1. Validates the source clip is in this production and READY.
   *    2. Snapshots the source clip into source_clip_snapshot.
   *    3. Resolves base + final prompt (re-runs Gemini merge if the
   *       partner sent only special_request without pre-merged text).
   *    4. INSERTs status='queued', then atomically flips to 'generating'
   *       under the per-team CLEAN_PLATE_CONCURRENT_CAP=5.
   *    5. Spawns the Modal clean_plate_job.
   *  Returns the inserted row. */
  async createCleanPlate(
    productionId: string,
    body: CreateCleanPlateRequest,
  ): Promise<CleanPlate> {
    const { data } = await request<CleanPlate>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/clean-plates`,
      { method: 'POST', body },
    );
    return data;
  },

  /** Re-dispatch a failed/ready/cancelled clean plate. Worker enforces
   *  the 5-attempt cap and the per-team concurrency cap. */
  async regenerateCleanPlate(
    productionId: string,
    cleanPlateId: string,
  ): Promise<{ ok: boolean }> {
    const { data } = await request<{ ok: boolean }>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/clean-plates/${encodeURIComponent(cleanPlateId)}/regenerate`,
      { method: 'POST' },
    );
    return data;
  },

  async deleteCleanPlate(productionId: string, cleanPlateId: string): Promise<void> {
    await request<unknown>(
      `/v1/sceneforge/productions/${encodeURIComponent(productionId)}/clean-plates/${encodeURIComponent(cleanPlateId)}`,
      { method: 'DELETE' },
    );
  },

  // -------------------------------------------------------------------------
  // V5 — Usage / Account stats
  // -------------------------------------------------------------------------

  async getUsage(
    opts?: { productionId?: string },
    signal?: AbortSignal,
  ): Promise<UsageSummary> {
    const reqOpts: RequestOptions = {};
    if (signal) reqOpts.signal = signal;
    const qs = new URLSearchParams();
    if (opts?.productionId) {
      qs.set('productionId', opts.productionId);
    }
    const path = qs.toString()
      ? `/v1/sceneforge/usage?${qs.toString()}`
      : '/v1/sceneforge/usage';
    const { data } = await request<UsageSummary>(path, reqOpts);
    return data;
  },
};
